import React, { useEffect, useState } from "react";
import { Pencil, ImagePlus, X, Save, Loader2 } from "lucide-react";
import { useEditMode } from "@/contexts/CmsContext";
import {
  commitStagedTexts,
  discardStagedTexts,
  subscribeStagedCount,
} from "@/lib/slotCache";

// Two header buttons + a sticky top banner.
// While in an edit mode, all navigation links on the page are blocked
// (the context owns that — see CmsContext.jsx).
const CmsEditButtons = () => {
  const { imageEditMode, textEditMode, toggleImage, toggleText } = useEditMode();

  const btnClass = (active) =>
    `hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-[9px] tracking-[0.22em] uppercase border transition-colors ${
      active
        ? "bg-[#D4A373] text-[#1A1513] border-[#D4A373]"
        : "border-[#2C2621]/15 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542]"
    }`;

  return (
    <div className="flex items-center gap-1.5" data-edit-allow="true">
      <button
        type="button"
        onClick={toggleImage}
        className={btnClass(imageEditMode)}
        data-testid="cms-toggle-image"
        aria-pressed={imageEditMode}
      >
        <ImagePlus className="w-3 h-3" strokeWidth={1.6} />
        Edit Images
      </button>
      <button
        type="button"
        onClick={toggleText}
        className={btnClass(textEditMode)}
        data-testid="cms-toggle-text"
        aria-pressed={textEditMode}
      >
        <Pencil className="w-3 h-3" strokeWidth={1.6} />
        Edit Text
      </button>
    </div>
  );
};

export const CmsEditBanner = () => {
  const { imageEditMode, textEditMode, toggleImage, toggleText } = useEditMode();
  const [stagedCount, setStagedCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [justSavedAt, setJustSavedAt] = useState(0);

  useEffect(() => subscribeStagedCount(setStagedCount), []);

  const active = imageEditMode || textEditMode;
  if (!active) return null;

  const label = textEditMode ? "Text edit mode" : "Image edit mode";
  const hint = textEditMode
    ? "Click any soft-amber text to edit. Edits are staged locally — hit Save to publish and auto-translate across English, French and Spanish. Esc reverts the current field."
    : "Click the pencil on any image to upload a new one, paste a URL, set alt text, or reset to the default. Navigation is paused while editing.";

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await commitStagedTexts();
      if (res?.saved) setJustSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  const handleExit = () => {
    if (textEditMode && stagedCount > 0) {
      const ok = window.confirm(
        `Discard ${stagedCount} unsaved text change${stagedCount === 1 ? "" : "s"} and exit Edit Mode?`
      );
      if (!ok) return;
      discardStagedTexts();
    }
    if (textEditMode) toggleText();
    else toggleImage();
  };

  const showSavedToast = textEditMode && justSavedAt && Date.now() - justSavedAt < 1800;

  return (
    <div className="cms-edit-banner" data-testid="cms-edit-banner" data-edit-allow="true">
      <span className="cms-edit-banner-label">{label}</span>
      <span className="cms-edit-banner-hint">{hint}</span>
      <div className="cms-edit-banner-actions" data-edit-allow="true">
        {textEditMode && (
          <>
            {showSavedToast && (
              <span
                className="cms-edit-banner-saved"
                data-testid="cms-edit-banner-saved"
              >
                Saved · translating in the background
              </span>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || stagedCount === 0}
              className={`cms-edit-banner-save ${stagedCount === 0 ? "is-disabled" : ""}`}
              data-edit-allow="true"
              data-testid="cms-edit-banner-save"
              aria-label={
                stagedCount === 0
                  ? "No unsaved changes"
                  : `Save ${stagedCount} change${stagedCount === 1 ? "" : "s"} and translate`
              }
            >
              {saving ? (
                <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.8} />
              ) : (
                <Save className="w-3 h-3" strokeWidth={1.8} />
              )}
              Save
              {stagedCount > 0 && !saving && (
                <span className="cms-edit-banner-save-count" data-testid="cms-edit-banner-staged-count">
                  {stagedCount}
                </span>
              )}
            </button>
          </>
        )}
        <button
          type="button"
          onClick={handleExit}
          className="cms-edit-banner-close"
          data-edit-allow="true"
          data-testid="cms-edit-banner-close"
        >
          <X className="w-3 h-3" strokeWidth={1.8} /> Exit
        </button>
      </div>
    </div>
  );
};

export default CmsEditButtons;
